const PROFESSIONAL_META = Object.freeze({
    PHOTOGRAPHER: { roleKey: "photographer", roleLabel: "摄影师" },
    MAKEUP_ARTIST: { roleKey: "makeup", roleLabel: "化妆师" },
});

const OPERATING_STATUS_LABELS = Object.freeze({
    ACTIVE: "正常接单",
    DISABLED: "暂停接单",
    EXITED: "已退出接单",
    PERMANENTLY_STOPPED: "永久停止接单",
});

const PROFESSIONAL_STATUS_LABELS = Object.freeze({
    NOT_ACTIVE: "专业身份未生效",
    ACTIVE: "专业身份有效",
    SUSPENDED_BY_REAL_NAME_REVOCATION: "实名变更后暂停",
    DISABLED_BY_ADMIN: "专业身份已停用",
});

const ACTIVE_PAYMENT_STATUSES = Object.freeze(["CREATING", "CREATE_UNKNOWN", "PENDING"]);
const TERMINAL_PAYMENT_STATUSES = Object.freeze(["CREATE_FAILED", "SUCCEEDED", "EXPIRED", "CANCELLED"]);
const CREATOR_DEPOSIT_INTENT_PREFIX = "ijust_creator_deposit_intent:";
const FUND_DETAIL_TYPE_LABELS = Object.freeze({
    PAYMENT: "缴纳",
    RESPONSIBILITY_DEDUCTION: "责任扣款",
    REVERSAL: "冲正",
    FUNDING_CORRECTION: "到账事实更正",
});

const PAYMENT_STATUS_LABELS = Object.freeze({
    CREATING: "创建中",
    CREATE_UNKNOWN: "确认中",
    PENDING: "待支付",
    CREATE_FAILED: "明确失败",
    SUCCEEDED: "成功",
    CANCELLED: "取消",
    EXPIRED: "过期",
});

const REFUND_STATUS_LABELS = Object.freeze({
    REQUESTED: "退款处理中",
    PROCESSING: "退款处理中",
    SUCCEEDED: "退款成功",
    MANUAL_REQUIRED: "退款待人工处理",
});

const asText = (value) => value === null || value === undefined ? "" : String(value).trim();
const asMoney = (value) => {
    const amount = Number(value);
    return Number.isFinite(amount) ? amount : 0;
};
const asNullableMoney = (value) => value === null || value === undefined || value === "" ? null : asMoney(value);
const normalizeProfessionalType = (value) => {
    const professionalType = asText(value).toUpperCase();
    return PROFESSIONAL_META[professionalType] ? professionalType : "";
};
const getCreatorDepositMeta = (value) => PROFESSIONAL_META[normalizeProfessionalType(value)] || { roleKey: "unknown", roleLabel: "专业身份" };

const normalizeCreatorDepositPaymentChannels = (items) => (Array.isArray(items) ? items : [])
    .filter((item) => item && Number.isInteger(item.channel))
    .map((item) => ({
        channel: item.channel,
        channelName: asText(item.channelName).toUpperCase(),
        channelDescription: asText(item.channelDescription),
    }));

const normalizeCreatorDepositPaymentRequest = (raw = {}) => ({
    paymentRequestNo: asText(raw.paymentRequestNo),
    paymentStatus: asText(raw.paymentStatus).toUpperCase(),
    statusLabel: PAYMENT_STATUS_LABELS[asText(raw.paymentStatus).toUpperCase()] || "确认中",
    channel: Number.isInteger(raw.channel) ? raw.channel : Number(raw.channel) || 0,
    channelName: asText(raw.channelName).toUpperCase(),
    channelDescription: asText(raw.channelDescription).replace(/\u62bc\u91d1/g, '保证金'),
    invocationPayload: asText(raw.invocationPayload),
    amount: asMoney(raw.amount),
    paidAmount: raw.paidAmount === null || raw.paidAmount === undefined ? null : asMoney(raw.paidAmount),
    creditedAmount: raw.creditedAmount === null || raw.creditedAmount === undefined ? null : asMoney(raw.creditedAmount),
    settledDebtAmount: raw.settledDebtAmount === null || raw.settledDebtAmount === undefined ? null : asMoney(raw.settledDebtAmount),
    currentBalance: raw.currentBalance === null || raw.currentBalance === undefined ? null : asMoney(raw.currentBalance),
    expiresAt: asText(raw.expiresAt),
    paidAt: asText(raw.paidAt),
    createdAt: asText(raw.createdAt),
});

const findActiveCreatorDepositPayment = (items) => (Array.isArray(items) ? items : [])
    .map(normalizeCreatorDepositPaymentRequest)
    .find((item) => ACTIVE_PAYMENT_STATUSES.includes(item.paymentStatus)) || null;

const parseWechatNativeCodeUrl = (payment = {}) => {
    if (asText(payment.paymentStatus).toUpperCase() !== "PENDING" || Number(payment.channel) !== 2) return "";
    try {
        const payload = JSON.parse(asText(payment.invocationPayload));
        const codeUrl = asText(payload && payload.codeUrl);
        return codeUrl.startsWith("weixin://") ? codeUrl : "";
    } catch (error) {
        return "";
    }
};

const buildCreatorDepositPaymentSubmission = (idempotencyKey, channel) => {
    const stableKey = asText(idempotencyKey);
    const numericChannel = Number(channel);
    if (!stableKey || stableKey.length > 128 || !Number.isInteger(numericChannel)) {
        throw new Error("请选择服务端提供的支付渠道后重试");
    }
    return { idempotencyKey: stableKey, channel: numericChannel };
};

const buildCreatorDepositPaymentSwitchSubmission = (sourcePaymentRequestNo, idempotencyKey, channel) => ({
    sourcePaymentRequestNo: asText(sourcePaymentRequestNo),
    ...buildCreatorDepositPaymentSubmission(idempotencyKey, channel),
});

const creatorDepositIntentStorageKey = (accountId, professionalType) => {
    const normalizedType = normalizeProfessionalType(professionalType);
    return `${CREATOR_DEPOSIT_INTENT_PREFIX}${encodeURIComponent(asText(accountId))}:${normalizedType}`;
};

const defaultIntentKeyFactory = () => `creator-deposit-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
const readCreatorDepositIntent = (storage, accountId, professionalType) => {
    if (!storage) return null;
    try {
        return JSON.parse(storage.getItem(creatorDepositIntentStorageKey(accountId, professionalType)) || "null");
    } catch (error) {
        return null;
    }
};
const getOrCreateCreatorDepositIntent = (storage, accountId, professionalType, channel, keyFactoryOrFingerprint = defaultIntentKeyFactory, intentFingerprint = "") => {
    const normalizedType = normalizeProfessionalType(professionalType);
    const normalizedAccountId = asText(accountId);
    const numericChannel = Number(channel);
    const keyFactory = typeof keyFactoryOrFingerprint === "function" ? keyFactoryOrFingerprint : defaultIntentKeyFactory;
    const fingerprint = typeof keyFactoryOrFingerprint === "function" ? asText(intentFingerprint) : asText(keyFactoryOrFingerprint);
    if (!storage || !normalizedAccountId || !normalizedType || !Number.isInteger(numericChannel)) {
        throw new Error("无法确认当前缴纳意图，请重新登录后重试");
    }
    const storageKey = creatorDepositIntentStorageKey(normalizedAccountId, normalizedType);
    const staleAccountKeys = [];
    for (let index = 0; index < storage.length; index += 1) {
        const key = storage.key(index);
        if (key && key.startsWith(CREATOR_DEPOSIT_INTENT_PREFIX) && key !== storageKey) {
            try {
                const stored = JSON.parse(storage.getItem(key) || "null");
                if (stored && stored.accountId !== normalizedAccountId) staleAccountKeys.push(key);
            } catch (error) {
                staleAccountKeys.push(key);
            }
        }
    }
    staleAccountKeys.forEach((key) => storage.removeItem(key));
    try {
        const current = JSON.parse(storage.getItem(storageKey) || "null");
        if (current && current.accountId === normalizedAccountId && current.professionalType === normalizedType
            && current.channel === numericChannel && asText(current.intentFingerprint) === fingerprint && asText(current.idempotencyKey)) {
            return current.idempotencyKey;
        }
    } catch (error) {
        storage.removeItem(storageKey);
    }
    const idempotencyKey = asText(keyFactory()).slice(0, 128);
    storage.setItem(storageKey, JSON.stringify({
        accountId: normalizedAccountId,
        professionalType: normalizedType,
        channel: numericChannel,
        intentFingerprint: fingerprint,
        idempotencyKey,
    }));
    return idempotencyKey;
};

const rememberCreatorDepositPaymentRequest = (storage, accountId, professionalType, payment) => {
    const storageKey = creatorDepositIntentStorageKey(accountId, professionalType);
    const current = readCreatorDepositIntent(storage, accountId, professionalType);
    if (!storage || !current || !payment || !asText(payment.paymentRequestNo)) return;
    storage.setItem(storageKey, JSON.stringify({ ...current, paymentRequestNo: asText(payment.paymentRequestNo) }));
};

const releaseCreatorDepositIntent = (storage, accountId, professionalType) => {
    if (!storage) return;
    storage.removeItem(creatorDepositIntentStorageKey(accountId, professionalType));
};

const clearCreatorDepositIntents = (storage) => {
    if (!storage) return;
    const keys = [];
    for (let index = 0; index < storage.length; index += 1) {
        const key = storage.key(index);
        if (key && key.startsWith(CREATOR_DEPOSIT_INTENT_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => storage.removeItem(key));
};

const parseCreatorDepositUnknownResponse = (error) => {
    const response = error && (error.response || error);
    const payload = response && response.data;
    if (!response || Number(response.status) !== 202 || !payload || Number(payload.code) !== 202) return null;
    const payment = normalizeCreatorDepositPaymentRequest(payload.data);
    return payment.paymentStatus === "CREATE_UNKNOWN" && payment.paymentRequestNo ? payment : null;
};

const isCreatorDepositPaymentTerminal = (payment) => TERMINAL_PAYMENT_STATUSES.includes(asText(payment && payment.paymentStatus).toUpperCase());

const normalizeCreatorDepositAccount = (raw = {}) => {
    const professionalType = normalizeProfessionalType(raw.professionalType);
    const meta = PROFESSIONAL_META[professionalType] || { roleKey: "unknown", roleLabel: "专业身份" };
    const operatingStatus = asText(raw.operatingStatus).toUpperCase();
    const professionalStatus = asText(raw.professionalStatus).toUpperCase();
    const debtAmount = asMoney(raw.debtAmount);
    return {
        professionalType,
        ...meta,
        balance: asMoney(raw.balance),
        debtAmount,
        frozenAmount: asMoney(raw.frozenAmount),
        frozen: raw.frozen === true,
        operatingStatus,
        operatingStatusLabel: OPERATING_STATUS_LABELS[operatingStatus] || "运营状态待确认",
        professionalStatus,
        professionalStatusLabel: PROFESSIONAL_STATUS_LABELS[professionalStatus] || "专业状态待确认",
        eligibleForNewOrders: raw.eligibleForNewOrders === true,
        eligibilityTip: asText(raw.eligibilityTip).replace(/\u62bc\u91d1/g, '保证金'),
        hasDebt: debtAmount > 0,
    };
};

const normalizeAgreement = (raw = {}) => ({
    configured: raw.configured === true,
    version: asText(raw.version),
    fileUrl: asText(raw.fileUrl),
    accepted: raw.accepted === true,
    acceptanceRequired: raw.acceptanceRequired === true,
    acceptedAt: asText(raw.acceptedAt),
});

const normalizeCreatorDepositPreview = (raw = {}) => {
    const professionalType = normalizeProfessionalType(raw.professionalType);
    const totalDue = asMoney(raw.totalDue);
    const debtAmount = asMoney(raw.debtAmount);
    return {
        professionalType,
        currentBalance: asMoney(raw.currentBalance),
        debtAmount,
        topUpAmount: asMoney(raw.topUpAmount),
        totalDue,
        frozenAmount: asMoney(raw.frozenAmount),
        frozen: raw.frozen === true,
        eligibleForNewOrders: raw.eligibleForNewOrders === true,
        eligibilityTip: asText(raw.eligibilityTip).replace(/\u62bc\u91d1/g, '保证金'),
        paymentAvailable: raw.paymentAvailable === true,
        paymentUnavailableReason: asText(raw.paymentUnavailableReason).replace(/\u62bc\u91d1/g, '保证金'),
        agreement: normalizeAgreement(raw.agreement),
        showNoPaymentDue: totalDue === 0,
        showFrozenWarning: raw.frozen === true,
    };
};

const getCreatorDepositPaymentPresentation = (account, preview = {}) => {
    const loadedAccount = account || {};
    const agreement = preview.agreement || {};
    const totalDue = asMoney(preview.totalDue);
    const hasAuthoritativePair = !!loadedAccount.professionalType && loadedAccount.professionalType === preview.professionalType;
    return {
        debtOnly: ["EXITED", "PERMANENTLY_STOPPED"].includes(loadedAccount.operatingStatus) && asMoney(preview.debtAmount) > 0,
        showNoPaymentDue: totalDue === 0,
        showFrozenWarning: preview.frozen === true,
        canAcceptAgreement: agreement.configured === true && agreement.accepted !== true && !!agreement.version && !!agreement.fileUrl,
        canContinueToPayment: hasAuthoritativePair && preview.paymentAvailable === true && totalDue > 0,
    };
};

const buildCreatorDepositAgreementAcceptance = (preview = {}) => {
    const agreement = preview.agreement || {};
    if (agreement.configured !== true || !asText(agreement.version) || !asText(agreement.fileUrl)) {
        throw new Error("当前履约保证金协议未配置，暂不可同意");
    }
    return {
        agreementVersion: asText(agreement.version),
        accepted: true,
    };
};

const normalizeCreatorDepositFundDetail = (raw = {}) => {
    const detailType = asText(raw.detailType).toUpperCase();
    const status = asText(raw.status).toUpperCase();
    const amount = asMoney(raw.amount);
    const refundAmount = asNullableMoney(raw.refundAmount);
    const hasRefund = refundAmount !== null && refundAmount > 0;
    return {
        detailNo: asText(raw.detailNo),
        detailType,
        typeLabel: FUND_DETAIL_TYPE_LABELS[detailType] || "资金变动",
        amount,
        direction: amount > 0 ? "increase" : amount < 0 ? "decrease" : "neutral",
        paidAmount: asNullableMoney(raw.paidAmount),
        creditedAmount: asNullableMoney(raw.creditedAmount),
        settledDebtAmount: asNullableMoney(raw.settledDebtAmount),
        refundAmount,
        hasRefund,
        status,
        statusLabel: hasRefund
            ? (REFUND_STATUS_LABELS[status] || "退款处理中")
            : (status === "COMPLETED" ? "已完成" : (PAYMENT_STATUS_LABELS[status] || "处理中")),
        occurredAt: asText(raw.occurredAt),
    };
};

const normalizeCreatorDepositFundDetails = (raw) => (Array.isArray(raw) ? raw : []).map(normalizeCreatorDepositFundDetail);
const getRecentCreatorDepositFundDetails = (details, expanded = false) => {
    const list = Array.isArray(details) ? details : [];
    return expanded ? list : list.slice(0, 5);
};

const normalizeCreatorDepositPayment = (raw = {}) => {
    const paymentStatus = asText(raw.paymentStatus).toUpperCase();
    return {
        paymentRequestNo: asText(raw.paymentRequestNo),
        paymentStatus,
        statusLabel: PAYMENT_STATUS_LABELS[paymentStatus] || "确认中",
        channel: Number(raw.channel) || 0,
        channelName: asText(raw.channelName),
        channelDescription: asText(raw.channelDescription).replace(/\u62bc\u91d1/g, '保证金') || "支付渠道待确认",
        amount: asMoney(raw.amount),
        createdAt: asText(raw.createdAt),
        paidAt: asText(raw.paidAt),
        expiresAt: asText(raw.expiresAt),
    };
};

const normalizeCreatorDepositPayments = (raw) => (Array.isArray(raw) ? raw : []).map(normalizeCreatorDepositPayment);

module.exports = {
    ACTIVE_PAYMENT_STATUSES,
    buildCreatorDepositAgreementAcceptance,
    buildCreatorDepositPaymentSubmission,
    buildCreatorDepositPaymentSwitchSubmission,
    clearCreatorDepositIntents,
    findActiveCreatorDepositPayment,
    getRecentCreatorDepositFundDetails,
    getCreatorDepositMeta,
    getCreatorDepositPaymentPresentation,
    getOrCreateCreatorDepositIntent,
    isCreatorDepositPaymentTerminal,
    normalizeCreatorDepositAccount,
    normalizeCreatorDepositPaymentChannels,
    normalizeCreatorDepositPaymentRequest,
    normalizeCreatorDepositFundDetails,
    normalizeCreatorDepositPayments,
    normalizeCreatorDepositPreview,
    normalizeProfessionalType,
    parseCreatorDepositUnknownResponse,
    parseWechatNativeCodeUrl,
    readCreatorDepositIntent,
    rememberCreatorDepositPaymentRequest,
    releaseCreatorDepositIntent,
};
